/**
 * Class assets — the single source of truth for "what does this class actually hold?"
 *
 * Two callers, one walk of the data:
 *   - ClassCard renders the count pills from the summary.
 *   - The batch download builds its zip from `downloadable`.
 *
 * They must never disagree: a card that says "3 videos" and a zip that contains
 * two is the bug this file exists to prevent.
 *
 * What counts as downloadable
 *   Only files we host. Tests and assessments are references to a test id, not
 *   an artifact, so they are counted and listed in the manifest but never
 *   fetched. External items (YouTube and anything else pointing off our CDN)
 *   are recorded as links for the manifest and skipped by the downloader.
 */

import { ClassConfig, ContentItem, SectionContent, SectionType } from '../types/lessonPlannerTypes';
import { cdnService } from '@/services/cdnService';

/** Where a file lands inside the class folder. */
export type AssetGroup = 'Videos' | 'Recordings' | 'Reading-Material';

export interface ClassAsset {
  id: string;
  title: string;
  /** Absolute, fetchable URL (CDN path resolved, signed recording URLs passed through). */
  url: string;
  /** Name to write into the zip, extension included. */
  fileName: string;
  section: SectionType;
  group: AssetGroup;
  /** Recordings come from Teams; manual uploads come from Add Video / the content picker. */
  isRecording: boolean;
  sizeBytes?: number;
}

/** An item we deliberately do not fetch — YouTube, Vimeo, any off-CDN link. */
export interface ExternalRef {
  title: string;
  url: string;
  section: SectionType;
}

/** A test or assessment: counted, listed, never downloaded. */
export interface NonFileRef {
  title: string;
  kind: 'test' | 'assessment';
  section: SectionType;
}

export interface ClassAssetSummary {
  /** Manual video uploads, excluding Teams recordings. */
  videos: number;
  /** Teams class recordings. */
  recordings: number;
  /** Reading material and other document files. */
  documents: number;
  tests: number;
  assessments: number;
  /** Files the downloader will fetch. */
  downloadable: ClassAsset[];
  /** Links for the manifest. */
  external: ExternalRef[];
  /** Tests and assessments for the manifest. */
  nonFiles: NonFileRef[];
  /** Sum of known sizes; items without a size contribute nothing, so treat it as a floor. */
  knownBytes: number;
  /** True when at least one downloadable item has no size on record. */
  hasUnknownSizes: boolean;
}

const SECTIONS: { key: SectionType; source: (c: ClassConfig) => SectionContent | undefined }[] = [
  { key: 'preClass', source: (c) => c.preClass },
  { key: 'liveClass', source: (c) => c.liveClass },
  { key: 'postClass', source: (c) => c.postClass },
];

const EXTERNAL_HOSTS = /(?:youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com|drive\.google\.com)/i;

/**
 * An item is external when the author said so, when it carries a YouTube URL,
 * or when the only URL we have points at somebody else's host. The last check
 * matters because older rows predate the `types` field.
 */
const isExternal = (item: any): boolean => {
  if (item?.types === 'external') return true;
  if (item?.youtubeUrl) return true;
  const url = item?.fileUrl || item?.sourceId || '';
  return typeof url === 'string' && EXTERNAL_HOSTS.test(url);
};

const externalUrl = (item: any): string =>
  item?.youtubeUrl || item?.fileUrl || item?.sourceId || '';

/** Absolute URL for a hosted item, or an empty string when the row has nothing to fetch. */
const resolveUrl = (item: any): string => {
  const raw = item?.fileUrl || item?.sourceId || '';
  if (!raw || typeof raw !== 'string') return '';
  return cdnService.buildCDNUrl(raw);
};

const sizeOf = (item: any): number | undefined => {
  const bytes = item?.fileSizeBytes ?? item?.file_size_bytes;
  if (typeof bytes === 'number' && bytes > 0) return bytes;
  // fileSize is stored in MB by the upload handler.
  const mb = item?.fileSize;
  if (typeof mb === 'number' && mb > 0) return Math.round(mb * 1024 * 1024);
  return undefined;
};

/** Windows and zip readers both choke on these; strip rather than fail late. */
export const sanitizeName = (name: string): string =>
  (name || 'untitled')
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'untitled';

const extensionFrom = (url: string, fallback: string): string => {
  // Signed URLs carry a query string; the extension sits before it.
  const path = url.split('?')[0].split('#')[0];
  const last = path.split('/').pop() || '';
  const dot = last.lastIndexOf('.');
  if (dot > 0 && dot < last.length - 1) {
    const ext = last.slice(dot + 1).toLowerCase();
    if (/^[a-z0-9]{1,5}$/.test(ext)) return ext;
  }
  return fallback;
};

/**
 * Build the zip entry name. Recordings already carry the storage-convention
 * file_name, which is what the teacher sees elsewhere, so it wins over the
 * display title.
 */
const buildFileName = (item: any, url: string, isVideo: boolean): string => {
  const existing = item?.file_name || item?.fileName;
  if (existing && /\.[a-z0-9]{1,5}$/i.test(existing)) return sanitizeName(existing);

  const base = sanitizeName(existing || item?.title || 'file');
  const ext = extensionFrom(url, isVideo ? 'mp4' : 'pdf');
  return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
};

/**
 * Walk one class and classify everything in it.
 *
 * Safe against a half-loaded meeting: a missing section is an empty section,
 * not a crash, because the card renders before CDN hydration settles.
 */
export const summarizeClassAssets = (classConfig: ClassConfig): ClassAssetSummary => {
  const summary: ClassAssetSummary = {
    videos: 0,
    recordings: 0,
    documents: 0,
    tests: 0,
    assessments: 0,
    downloadable: [],
    external: [],
    nonFiles: [],
    knownBytes: 0,
    hasUnknownSizes: false,
  };

  const seenUrls = new Set<string>();

  const addAsset = (item: any, section: SectionType, group: AssetGroup, isRecording: boolean) => {
    const url = resolveUrl(item);
    if (!url) return;
    // A recording can be listed under its own class and again as an orphan
    // matched by Teams id; the same file must not be zipped twice.
    if (seenUrls.has(url)) return;
    seenUrls.add(url);

    const bytes = sizeOf(item);
    if (bytes) summary.knownBytes += bytes;
    else summary.hasUnknownSizes = true;

    summary.downloadable.push({
      id: String(item?.id ?? url),
      title: item?.title || 'Untitled',
      url,
      fileName: buildFileName(item, url, group !== 'Reading-Material'),
      section,
      group,
      isRecording,
      sizeBytes: bytes,
    });
  };

  for (const { key, source } of SECTIONS) {
    const section = source(classConfig) as any;
    if (!section) continue;

    for (const video of (section.videos || []) as ContentItem[]) {
      const recording = (video as any)?.source === 'recording';
      if (recording) summary.recordings += 1;
      else summary.videos += 1;

      if (isExternal(video)) {
        summary.external.push({ title: video.title || 'Untitled', url: externalUrl(video), section: key });
        continue;
      }
      addAsset(video, key, recording ? 'Recordings' : 'Videos', recording);
    }

    // `content` is reading material; `files` is the older key some rows still use.
    const documents = [
      ...((section.content || []) as ContentItem[]),
      ...((section.files || []) as ContentItem[]),
    ];
    for (const doc of documents) {
      summary.documents += 1;
      if (isExternal(doc)) {
        summary.external.push({ title: doc.title || 'Untitled', url: externalUrl(doc), section: key });
        continue;
      }
      addAsset(doc, key, 'Reading-Material', false);
    }

    for (const test of (section.tests || []) as ContentItem[]) {
      summary.tests += 1;
      summary.nonFiles.push({
        title: test.TestName || test.title || 'Untitled test',
        kind: 'test',
        section: key,
      });
    }

    for (const assessment of (section.assessments || []) as ContentItem[]) {
      summary.assessments += 1;
      summary.nonFiles.push({
        title: assessment.title || 'Untitled assessment',
        kind: 'assessment',
        section: key,
      });
    }
  }

  return summary;
};

/** Human size for the pre-download confirmation. */
export const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

/**
 * Folder name for a section inside the zip, numbered so a file browser sorts
 * them the way the class ran rather than alphabetically.
 */
export const SECTION_FOLDER: Record<SectionType, string> = {
  preClass: '01-Pre-Class',
  liveClass: '02-Live-Class',
  postClass: '03-Post-Class',
};
